import { Link, useNavigate, useLocation } from 'react-router-dom'
import { ShoppingCart, LogOut, ChevronDown, ChevronLeft, ChevronRight, ExternalLink, Menu, X, Home, BookOpen, Bell, User, Calendar, Search, Phone, MessageCircle } from 'lucide-react'
import { useState, useEffect, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { cartService } from '@/services/cartService'
import { useAuthStore } from '@/stores/authStore'
import { bannerService, BannerData } from '@/services/bannerService'
import { Banner } from '@/components/Banner'
import { BRANDING } from '@/config/branding'
import { TENANT_LANDING } from '@/config/tenantLanding'
import { buildDistrictDashboardUrl } from '@/lib/districtDashboard'
import { rememberPostAuthReturnUrl } from '@/lib/returnUrl'
import { LanguageSwitcher } from './LanguageSwitcher'

type InsightsSubmenuPlacement = 'right' | 'left' | 'below'

const INSIGHTS_SUBMENU_WIDTH = 288
const VIEWPORT_PADDING = 16

export function Header() {
  const { t } = useTranslation('nav')
  const { t: tc } = useTranslation('common')
  const { enableCommunity = true, externalCalendarUrl: calendarFlagOn = false, externalCatalogUrl: catalogFlagOn = false } = useFlags()
  const externalCalendarUrl = calendarFlagOn ? TENANT_LANDING.externalCalendarUrl : null
  const externalCatalogUrl  = catalogFlagOn  ? TENANT_LANDING.externalCatalogUrl  : null
  const { onDemandOnly } = TENANT_LANDING
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
  const [isInsightsSubmenuOpen, setIsInsightsSubmenuOpen] = useState(false)
  const [insightsSubmenuPlacement, setInsightsSubmenuPlacement] = useState<InsightsSubmenuPlacement>('right')
  const [isSessionsDropdownOpen, setIsSessionsDropdownOpen] = useState(false)
  const [isEmployeeSubmenuOpen, setIsEmployeeSubmenuOpen] = useState(false)
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false)
  const [cartCount, setCartCount] = useState(0)
  const [profilePicture, setProfilePicture] = useState<string | null>(null)
  const [activeBanner, setActiveBanner] = useState<BannerData | null>(null)
  const [notificationItems, setNotificationItems] = useState<BannerData[]>([])
  const [showBanner, setShowBanner] = useState(false)
  const [dismissingBannerId, setDismissingBannerId] = useState<string | null>(null)
  const { isAuthenticated, user, legacyRoles, logout, checkAuth, fetchLegacyRoles } = useAuthStore()
  const navigate = useNavigate()
  const location = useLocation()
  const loginReturnTo = /^\/(login|register|forgot-password|reset-password|verify-email)(\/|$)/i.test(location.pathname)
    ? undefined : `${location.pathname}${location.search}${location.hash}`
  const prepareLogin = () => {
    if (loginReturnTo) {
      // This click is the latest navigation intent; abandoned checkout intents must
      // not replay after login. A later checkout action can still set its own.
      try {
        localStorage.removeItem('pendingAction')
        localStorage.removeItem('redirectAfterLogin')
      } catch { /* The destination lives in router state. */ }
      rememberPostAuthReturnUrl(loginReturnTo)
    }
    setIsMenuOpen(false)
  }
  const sessionsDropdownRef = useRef<HTMLDivElement>(null)
  const notificationsRef = useRef<HTMLDivElement>(null)
  const insightsSubmenuTriggerRef = useRef<HTMLDivElement>(null)
  const districtDashboardUrl = buildDistrictDashboardUrl(user?.sid ?? '')

  const updateInsightsSubmenuPlacement = useCallback(() => {
    const trigger = insightsSubmenuTriggerRef.current
    if (!trigger) return

    const triggerRect = trigger.getBoundingClientRect()
    const submenuWidth = Math.min(
      INSIGHTS_SUBMENU_WIDTH,
      Math.max(0, window.innerWidth - (VIEWPORT_PADDING * 2)),
    )
    const spaceToRight = window.innerWidth - triggerRect.right - VIEWPORT_PADDING
    const spaceToLeft = triggerRect.left - VIEWPORT_PADDING

    if (spaceToRight >= submenuWidth) {
      setInsightsSubmenuPlacement('right')
    } else if (spaceToLeft >= submenuWidth) {
      setInsightsSubmenuPlacement('left')
    } else {
      setInsightsSubmenuPlacement('below')
    }
  }, [])

  const openInsightsSubmenu = () => {
    updateInsightsSubmenuPlacement()
    setIsInsightsSubmenuOpen(true)
  }

  const toggleInsightsSubmenu = () => {
    if (!isInsightsSubmenuOpen) updateInsightsSubmenuPlacement()
    setIsInsightsSubmenuOpen(isOpen => !isOpen)
  }

  // Fetch profile picture when user changes
  useEffect(() => {
    const fetchProfilePicture = async () => {
      if (user?.profilePictureUrl) {
        setProfilePicture(user.profilePictureUrl)
      } else if (user?.email) {
        try {
          // Dynamically import to avoid circular dependency
          const { userProfileService } = await import('@/services/userProfileService')
          const profile = await userProfileService.getUserProfile()
          if (profile.profilePictureBase64 && profile.profilePictureContentType) {
            const dataUrl = `data:${profile.profilePictureContentType};base64,${profile.profilePictureBase64}`
            setProfilePicture(dataUrl)
          }
        } catch (error) {
          console.error('Error loading profile picture:', error)
        }
      }
    }

    if (isAuthenticated) {
      fetchProfilePicture()
    } else {
      setProfilePicture(null)
    }
  }, [user, isAuthenticated])

  // Listen for profile picture updates
  useEffect(() => {
    const handleProfileUpdate = async () => {
      if (user?.email) {
        try {
          const { userProfileService } = await import('@/services/userProfileService')
          const profile = await userProfileService.getUserProfile()
          if (profile.profilePictureBase64 && profile.profilePictureContentType) {
            const dataUrl = `data:${profile.profilePictureContentType};base64,${profile.profilePictureBase64}`
            setProfilePicture(dataUrl)
          }
        } catch (error) {
          console.error('Error loading profile picture:', error)
        }
      }
    }
    
    window.addEventListener('profilePictureUpdated', handleProfileUpdate)
    return () => window.removeEventListener('profilePictureUpdated', handleProfileUpdate)
  }, [user])

  // Check authentication on mount ONLY (but skip on auth pages to avoid clearing errors)
  useEffect(() => {
    const isAuthPage = ['/login', '/register', '/verify-email', '/reset-password'].includes(location.pathname)
    
    if (!isAuthPage) {
      const initAuth = async () => {
        await checkAuth()
      }
      initAuth()
    }
  }, [])

  useEffect(() => {
    if (isAuthenticated && user?.sid) {
      void fetchLegacyRoles()
    }
  }, [isAuthenticated, user?.sid, fetchLegacyRoles])

  useEffect(() => {
    if (!isInsightsSubmenuOpen) return

    updateInsightsSubmenuPlacement()
    window.addEventListener('resize', updateInsightsSubmenuPlacement)
    return () => window.removeEventListener('resize', updateInsightsSubmenuPlacement)
  }, [isInsightsSubmenuOpen, updateInsightsSubmenuPlacement])
  
  // Update cart count
  useEffect(() => {
    const updateCartCount = () => {
      const count = cartService.getCartCount()
      setCartCount(count)
    }

    // Initial load
    updateCartCount()

    // Listen for storage changes (when cart is updated in other tabs)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'escworks-cart') {
        updateCartCount()
      }
    }

    window.addEventListener('storage', handleStorageChange)

    // Listen for custom cart update events
    const handleCartUpdate = () => {
      updateCartCount()
    }

    window.addEventListener('cartUpdated', handleCartUpdate)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('cartUpdated', handleCartUpdate)
    }
  }, [])

  // Fetch active banner with polling (every 10 seconds)
  useEffect(() => {
    let isCurrent = true

    const fetchBanner = async () => {
      const response = await bannerService.getActiveBanner()
      if (!isCurrent) {
        return
      }

      if (response.success) {
        const items = response.banners ?? (response.banner ? [response.banner] : [])
        const nextBanner = response.banner ?? items[0] ?? null

        setNotificationItems(items)
        setActiveBanner(nextBanner)
        setShowBanner(Boolean(nextBanner))
      } else {
        setNotificationItems([])
        setActiveBanner(null)
        setShowBanner(false)
      }
    }

    // Initial fetch
    fetchBanner()

    // Poll for banner updates every 10 seconds
    const pollInterval = setInterval(() => {
      fetchBanner()
    }, 10000) // Poll every 10 seconds

    const handleNotificationRefresh = () => {
      void fetchBanner()
    }

    window.addEventListener('bannerNotificationsUpdated', handleNotificationRefresh)

    return () => {
      isCurrent = false
      clearInterval(pollInterval)
      window.removeEventListener('bannerNotificationsUpdated', handleNotificationRefresh)
    }
  }, [isAuthenticated, user?.id])

  const handleDismissBanner = async (bannerId: string) => {
    if (!isAuthenticated || dismissingBannerId) {
      return
    }

    try {
      setDismissingBannerId(bannerId)
      const response = await bannerService.dismissBanner(bannerId)

      if (response.success && response.dismissed) {
        const nextItems = notificationItems.filter(item => item.id !== bannerId)
        const nextBanner = activeBanner?.id === bannerId ? nextItems[0] ?? null : activeBanner

        setNotificationItems(nextItems)
        setActiveBanner(nextBanner)
        setShowBanner(Boolean(nextBanner))
      }
    } catch (error) {
      const status = typeof error === 'object' && error !== null && 'response' in error
        ? (error as { response?: { status?: number } }).response?.status
        : undefined

      if (status === 404) {
        const nextItems = notificationItems.filter(item => item.id !== bannerId)
        const nextBanner = activeBanner?.id === bannerId ? nextItems[0] ?? null : activeBanner

        setNotificationItems(nextItems)
        setActiveBanner(nextBanner)
        setShowBanner(Boolean(nextBanner))
      } else {
        console.error('Error dismissing banner:', error)
      }
    } finally {
      setDismissingBannerId(null)
    }
  }

  // Close notifications dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isNotificationsOpen && notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
        setIsNotificationsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isNotificationsOpen])


  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isUserMenuOpen && !(event.target as Element).closest('.user-menu-container')) {
        setIsUserMenuOpen(false)
        setIsInsightsSubmenuOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isUserMenuOpen])

  // Close sessions dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isSessionsDropdownOpen && sessionsDropdownRef.current && !sessionsDropdownRef.current.contains(event.target as Node)) {
        setIsSessionsDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isSessionsDropdownOpen])

  const handleLogout = async () => {
    try {
      await logout()
      setIsUserMenuOpen(false)
      setIsInsightsSubmenuOpen(false)
      // Redirect to home page
      navigate('/')
    } catch (error) {
      console.error('Error during logout:', error)
    }
  }

  const dark = BRANDING.darkHeader
  const headerNav = Boolean(TENANT_LANDING.headerNavClass)
  const identityHeader = Boolean(TENANT_LANDING.headerSchoolName)

  // Reusable class fragments based on header darkness
  const navBase = TENANT_LANDING.headerNavClass
    ?? 'inline-flex items-center space-x-1 xl:space-x-1.5 2xl:space-x-2 px-2 xl:px-3 2xl:px-4 py-2 text-sm xl:text-base 2xl:text-lg font-medium rounded-lg transition-all whitespace-nowrap'
  const navActive = TENANT_LANDING.headerNavActiveClass
    ?? (dark ? 'bg-white/20 text-white shadow-inner' : 'bg-primary-100 text-primary-700 shadow-inner')
  const navInactive = TENANT_LANDING.headerNavInactiveClass
    ?? (dark ? 'text-white/90 hover:bg-white/10 hover:text-white' : 'text-primary-700 hover:bg-primary-50 hover:text-primary-900')
  const navIconClass = identityHeader ? 'hidden' : 'h-4 w-4 xl:h-4 xl:w-4 2xl:h-5 2xl:w-5'
  const iconBtn = identityHeader
    ? 'p-2 rounded-lg transition-all text-primary-500 hover:bg-primary-50 hover:text-primary-500'
    : headerNav
    ? 'rounded-full p-2 text-ar-navy/80 transition-colors hover:bg-white/70 hover:text-ar-navy'
    : dark ? 'p-2 rounded-lg transition-all text-white/90 hover:bg-white/15 hover:text-white' : 'p-2 rounded-lg transition-all text-primary-700 hover:bg-primary-50 hover:text-primary-900'
  const avatarBtn = identityHeader
    ? 'flex items-center justify-center w-10 h-10 rounded-full transition-all bg-primary-50 text-primary-500 hover:bg-primary-100 border border-primary-200 hover:shadow-md overflow-hidden'
    : headerNav
    ? 'ml-1 flex h-9 w-9 items-center justify-center rounded-full bg-ar-navy text-sm font-semibold text-white ring-2 ring-white/70 overflow-hidden'
    : dark
    ? 'flex items-center justify-center w-10 h-10 rounded-full transition-all bg-white/20 text-white hover:bg-white/30 border border-white/30 hover:shadow-md overflow-hidden'
    : 'flex items-center justify-center w-10 h-10 rounded-full transition-all bg-primary-100 text-primary-700 hover:bg-primary-200 border border-primary-200 hover:shadow-md overflow-hidden'
  const accountControl = `inline-flex items-center gap-2 min-h-11 px-2 py-1 rounded-lg text-sm font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${dark ? 'bg-slate-900 text-white hover:bg-slate-800' : 'text-gray-900 hover:bg-black/5'}`
  const mobileLinkText = dark ? 'rgba(255,255,255,0.95)' : BRANDING.primaryColor
  const mobileActive = dark ? 'bg-white/20 shadow-lg' : 'bg-primary-100'
  const mobileHover = dark ? 'hover:bg-white/10' : 'hover:bg-primary-50'
  const mobileBorder = dark ? 'border-t border-white/20' : 'border-t border-primary-200'
  const headerMenuItemClass = identityHeader
    ? 'block px-4 py-2 text-sm text-primary-500 hover:bg-primary-50 rounded-lg mx-1'
    : headerNav
    ? 'block px-4 py-2 text-sm font-manrope text-ar-navy/80 hover:bg-white/70 hover:text-ar-navy rounded-full mx-1'
    : 'block px-4 py-2 text-base xl:text-lg text-gray-700 hover:bg-primary-50 hover:text-primary-900 rounded-lg mx-1'
  const hasNotifications = notificationItems.length > 0
  const notificationCountLabel = notificationItems.length > 9 ? '9+' : String(notificationItems.length)
  const sessionsNavActive = ['/conferences', '/hybrid-events', '/ondemand'].some(path => (
    location.pathname === path || location.pathname.startsWith(`${path}/`)
  ))
  const getNotificationDotClass = (type?: BannerData['type']) => (
    type === 'error' ? 'bg-red-500' :
    type === 'warning' ? 'bg-yellow-500' :
    type === 'success' ? 'bg-green-500' : 'bg-blue-500'
  )
  const getNotificationLabel = (item: BannerData) => {
    if (item.notificationType === 'session-discussion') return tc('sessionDiscussion')
    if (item.notificationType === 'community-post') return tc('communityPost')
    return tc('activeAnnouncement')
  }

  return (
    <>
      {/* Banner */}
      {showBanner && activeBanner && (
        <Banner
          banner={activeBanner}
          onDismiss={isAuthenticated && user ? handleDismissBanner : undefined}
          isDismissing={dismissingBannerId === activeBanner.id}
        />
      )}

      <header
        className="w-full z-40"
        style={identityHeader
          ? { background: BRANDING.headerBackground, minHeight: '88px' }
          : { background: BRANDING.headerBackground, minHeight: '72px' }}
      >
        {/* Main Header */}
        <div className={identityHeader ? 'w-full min-h-[88px] py-2' : 'w-full min-h-[72px] py-2'}>
          <div className={`flex flex-wrap items-center justify-between gap-y-2 h-full ${identityHeader ? 'gap-2 px-3 lg:px-4' : 'px-4 lg:px-8'}`}>

            <div className={headerNav ? `flex items-center min-w-0 ${identityHeader ? 'w-full xl:w-auto xl:flex-1 gap-2 xl:gap-4' : 'gap-12 lg:gap-16'}` : 'contents'}>
            {/* Left: tenant app logo */}
            <div className={`flex items-center min-w-0 ${identityHeader ? 'shrink' : 'flex-shrink-0'}`}>
              <Link to="/" className={(TENANT_LANDING.headerWordmark || identityHeader) ? 'flex items-center gap-3 no-underline hover:no-underline' : 'flex items-center'}>
                <img
                  src={BRANDING.appLogo}
                  alt={BRANDING.appName}
                  className={`object-contain ${identityHeader ? 'h-14 w-14 shrink-0' : TENANT_LANDING.headerWordmark ? 'h-12 w-12 rounded-xl shadow-md' : 'h-8 lg:h-9 xl:h-10'}`}
                  onError={(e) => { e.currentTarget.style.display = 'none' }}
                />
                {identityHeader && (
                  <span className="min-w-0 max-w-[11rem] sm:max-w-[14rem] xl:max-w-[18rem]">
                    <p className="ss-site-header-school-name">{TENANT_LANDING.headerSchoolName}</p>
                    {TENANT_LANDING.headerTagline && (
                      <p className="ss-site-header-school-tagline">{TENANT_LANDING.headerTagline}</p>
                    )}
                  </span>
                )}
                {TENANT_LANDING.headerWordmark && (
                  <span
                    className={`${TENANT_LANDING.headerWordmarkClass ?? 'text-[20px] font-extrabold tracking-tight text-ar-navy'} no-underline hover:no-underline`}
                    style={TENANT_LANDING.headerWordmarkClass
                      ? { fontFamily: 'var(--font-manrope), Manrope, sans-serif' }
                      : { fontFamily: "var(--font-poppins), 'Poppins', sans-serif", fontWeight: 800 }}
                  >
                    {TENANT_LANDING.headerWordmark}
                    {TENANT_LANDING.headerWordmarkAccent && (
                      <span className={TENANT_LANDING.headerWordmarkAccentClass ?? 'text-ar-red'}>
                        {TENANT_LANDING.headerWordmarkAccent}
                      </span>
                    )}
                  </span>
                )}
              </Link>
            </div>

            {/* Desktop Navigation */}
            <nav className={headerNav
              ? `hidden min-w-0 items-center ${identityHeader ? 'gap-0.5 xl:flex' : 'gap-1 xl:flex'}`
              : 'hidden xl:flex items-center space-x-2 xl:space-x-3 2xl:space-x-4'}>
              <Link to="/" className={`${navBase} ${location.pathname === '/' ? navActive : navInactive}`}>
                <Home className={navIconClass} />
                <span>{t('home')}</span>
              </Link>

              {!onDemandOnly && (<>
              {externalCalendarUrl ? (
                <a href={externalCalendarUrl} target="_blank" rel="noopener noreferrer" className={`${navBase} ${navInactive}`}>
                  <Calendar className={navIconClass} />
                  <span>{t('calendar')}</span>
                </a>
              ) : (
                <Link to="/calendar" className={`${navBase} ${location.pathname === '/calendar' ? navActive : navInactive}`}>
                  <Calendar className={navIconClass} />
                  <span>{t('calendar')}</span>
                </Link>
              )}

              {externalCatalogUrl ? (
                <a href={externalCatalogUrl} target="_blank" rel="noopener noreferrer" className={`${navBase} ${navInactive}`}>
                  <Search className={navIconClass} />
                  <span>{t('catalog')}</span>
                </a>
              ) : (
                <Link to="/catalog" className={`${navBase} ${location.pathname === '/catalog' ? navActive : navInactive}`}>
                  <Search className={navIconClass} />
                  <span>{t('catalog')}</span>
                </Link>
              )}
              </>)}

              {onDemandOnly ? (
                <Link to="/ondemand" className={`${navBase} ${location.pathname.startsWith('/ondemand') ? navActive : navInactive}`}>
                  <BookOpen className={navIconClass} />
                  <span>{t('onDemand')}</span>
                </Link>
              ) : (
              /* Sessions Dropdown */
              <div ref={sessionsDropdownRef} className="relative">
                <button
                  onClick={() => setIsSessionsDropdownOpen(!isSessionsDropdownOpen)}
                  className={`${navBase} ${sessionsNavActive ? navActive : navInactive}`}
                >
                  <BookOpen className={`${navIconClass} mr-1 xl:mr-1.5 2xl:mr-2`} />
                  {t('sessions')}
                  <ChevronDown className={`ml-0.5 h-4 w-4 transition-transform ${isSessionsDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {isSessionsDropdownOpen && (
                  <div className="absolute left-0 mt-2 w-56 rounded-lg shadow-xl bg-white ring-1 ring-black ring-opacity-5 z-50">
                    <div className="py-1" role="menu">
                      <Link
                        to="/conferences"
                        className={headerMenuItemClass}
                        onClick={() => setIsSessionsDropdownOpen(false)}
                      >
                        {t('conferences')}
                      </Link>
                      {TENANT_LANDING.showHybridEvents && (
                        <Link
                          to="/hybrid-events"
                          className={headerMenuItemClass}
                          onClick={() => setIsSessionsDropdownOpen(false)}
                        >
                          {t('hybrid')}
                        </Link>
                      )}
                      <Link
                        to="/ondemand"
                        className={headerMenuItemClass}
                        onClick={() => setIsSessionsDropdownOpen(false)}
                      >
                        {t('onDemand')}
                      </Link>
                      {user?.isEmployee && (
                        <div className="relative">
                          <button
                            className={`w-full text-left ${headerMenuItemClass} flex items-center justify-between`}
                            onMouseEnter={() => setIsEmployeeSubmenuOpen(true)}
                            onMouseLeave={() => setIsEmployeeSubmenuOpen(false)}
                            onClick={() => setIsEmployeeSubmenuOpen(!isEmployeeSubmenuOpen)}
                          >
                            {t('employee')}
                            <ChevronDown className={`ml-1 h-4 w-4 transition-transform ${isEmployeeSubmenuOpen ? 'rotate-180' : ''}`} />
                          </button>
                          {isEmployeeSubmenuOpen && (
                            <div
                              className="absolute left-full top-0 ml-1 w-60 rounded-lg shadow-xl bg-white ring-1 ring-black ring-opacity-5"
                              onMouseEnter={() => setIsEmployeeSubmenuOpen(true)}
                              onMouseLeave={() => setIsEmployeeSubmenuOpen(false)}
                            >
                              <div className="py-1">
                                <Link
                                  to="/workforce-development-wednesday"
                                  className={`${headerMenuItemClass} whitespace-nowrap`}
                                  onClick={() => { setIsEmployeeSubmenuOpen(false); setIsSessionsDropdownOpen(false) }}
                                >
                                  {t('workforceDevelopment')}
                                </Link>
                                <Link
                                  to="/internal-sessions"
                                  className={`${headerMenuItemClass} whitespace-nowrap`}
                                  onClick={() => { setIsEmployeeSubmenuOpen(false); setIsSessionsDropdownOpen(false) }}
                                >
                                  {t('internal')}
                                </Link>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              )}

              {enableCommunity && (
                <Link to="/community" className={`${navBase} ${location.pathname === '/community' ? navActive : navInactive}`}>
                  <MessageCircle className={navIconClass} />
                  <span>{t('community')}</span>
                </Link>
              )}

              <a
                href="https://esc4.net/escdevelopworks/#alert"
                target="_blank"
                rel="noopener noreferrer"
                className={`${navBase} ${navInactive}`}
              >
                <Phone className={navIconClass} />
                <span>{t('contactUs')}</span>
              </a>
            </nav>
            </div>

            {/* Right Side — Notifications, Login and Cart */}
            <div className={`relative z-10 ml-auto flex max-w-full flex-wrap items-center justify-end ${identityHeader ? 'gap-1.5' : 'gap-2 lg:gap-3 xl:gap-4'}`}>

              {/* Notifications Bell */}
              {isAuthenticated && user && (
                <div ref={notificationsRef} className="relative">
                  <button
                    onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
                    className={`${iconBtn} relative ${hasNotifications ? 'bell-shake' : ''}`}
                    title={tc('notifications')}
                    aria-label={tc('notifications')}
                  >
                    <Bell className={`h-5 w-5 ${hasNotifications ? 'bell-shake text-yellow-400' : ''}`} />
                    {hasNotifications ? (
                      <span className="absolute -top-0.5 -right-0.5 min-w-[1rem] h-4 px-1 rounded-full bg-red-500 text-[10px] font-bold leading-4 text-white text-center" style={BRANDING.accentColor ? { backgroundColor: BRANDING.accentColor } : undefined}>
                        {notificationCountLabel}
                      </span>
                    ) : null}
                  </button>

                  {isNotificationsOpen && (
                    <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-[500px] overflow-hidden flex flex-col">
                      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                        <h3 className="text-sm font-semibold text-gray-900">{tc('notifications')}</h3>
                      </div>

                      <div className="flex-1 overflow-y-auto">
                        {hasNotifications ? (
                          <div>
                            <div className="px-4 py-2 bg-blue-50 border-b border-gray-200">
                              <p className="text-xs font-semibold text-blue-900 uppercase tracking-wide">{tc('recent')}</p>
                            </div>
                            <div className="divide-y divide-gray-100">
                              {notificationItems.map(item => (
                                <div key={item.id} className="px-4 py-3">
                                  <div className="flex items-start gap-3">
                                    <div className={`flex-shrink-0 w-2 h-2 rounded-full mt-1.5 ${getNotificationDotClass(item.type)}`} />
                                    <div className="flex-1 min-w-0">
                                      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1">
                                        {getNotificationLabel(item)}
                                      </p>
                                      <p className="text-sm text-gray-900 break-words">{item.message}</p>
                                      {item.link && item.linkText && (
                                        <a
                                          href={item.link}
                                          className="text-xs text-blue-600 hover:text-blue-800 mt-1 inline-block"
                                          target={item.link.startsWith('/') ? undefined : '_blank'}
                                          rel={item.link.startsWith('/') ? undefined : 'noopener noreferrer'}
                                          onClick={(event) => {
                                            setIsNotificationsOpen(false)
                                            if (item.link?.startsWith('/')) {
                                              event.preventDefault()
                                              navigate(item.link)
                                            }
                                          }}
                                        >
                                          {item.linkText} →
                                        </a>
                                      )}
                                    </div>
                                    {item.dismissible && (
                                      <button
                                        onClick={() => handleDismissBanner(item.id)}
                                        disabled={dismissingBannerId === item.id}
                                        className="flex-shrink-0 p-1 rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                                        aria-label={tc('dismiss')}
                                        aria-busy={dismissingBannerId === item.id}
                                      >
                                        <X className="h-4 w-4" />
                                      </button>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div className="px-4 py-8 text-center">
                            <Bell className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                            <p className="text-sm text-gray-500">{tc('noNotifications')}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {isAuthenticated && user ? (
                <div className="user-menu-container" style={{ position: 'static' }}>
                  <button
                    className={accountControl}
                    onClick={() => {
                      setIsUserMenuOpen(!isUserMenuOpen)
                      if (isUserMenuOpen) setIsInsightsSubmenuOpen(false)
                    }}
                    aria-label={t('myAccount')}
                    aria-expanded={isUserMenuOpen}
                    aria-controls="header-account-menu"
                    title={user?.name || user?.email || t('userFallback')}
                  >
                    <span className={`${avatarBtn} shrink-0`} aria-hidden="true">
                      {profilePicture ? (
                        <img src={profilePicture} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <User className="h-5 w-5" />
                      )}
                    </span>
                    <span>{t('myAccount')}</span>
                  </button>

                  {isUserMenuOpen && (
                    <div id="header-account-menu" className="absolute top-full right-0 z-50 mt-2 w-64 rounded-lg border border-gray-300 bg-white py-1 shadow-xl">
                      <div className="px-4 py-3 border-b border-gray-300">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 bg-gray-100">
                            {profilePicture ? (
                              <img src={profilePicture} alt={user.name || t('userFallback')} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <User className="h-5 w-5 text-gray-400" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">{user?.name || user?.email || t('userFallback')}</div>
                            <div className="text-xs text-gray-500 mt-1 truncate">{user?.email || tc('noEmailAvailable')}</div>
                          </div>
                        </div>
                      </div>

                      <div className="px-4 py-2 border-b border-gray-300">
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{t('myAccount')}</div>
                        <div className="space-y-1">
                          <Link to="/transcript" className="block px-3 py-2 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-900 rounded-md transition-colors" onClick={() => setIsUserMenuOpen(false)}>{t('transcripts')}</Link>
                          <Link to="/user-profile" className="block px-3 py-2 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-900 rounded-md transition-colors" onClick={() => setIsUserMenuOpen(false)}>{t('userProfile')}</Link>
                          <Link to="/registration-history" className="block px-3 py-2 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-900 rounded-md transition-colors" onClick={() => setIsUserMenuOpen(false)}>{t('registrationCertificate')}</Link>
                          {legacyRoles?.dashboardSi && (
                            <div
                              ref={insightsSubmenuTriggerRef}
                              className="relative"
                              onMouseEnter={openInsightsSubmenu}
                              onMouseLeave={() => setIsInsightsSubmenuOpen(false)}
                            >
                              <button
                                type="button"
                                className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-gray-700 transition-colors hover:bg-primary-50 hover:text-primary-900"
                                onClick={toggleInsightsSubmenu}
                                aria-haspopup="menu"
                                aria-expanded={isInsightsSubmenuOpen}
                              >
                                {insightsSubmenuPlacement === 'left' ? (
                                  <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                                ) : insightsSubmenuPlacement === 'below' ? (
                                  <ChevronDown className="h-4 w-4" aria-hidden="true" />
                                ) : (
                                  <ChevronRight className="h-4 w-4" aria-hidden="true" />
                                )}
                                {t('myInsights')}
                              </button>

                              {isInsightsSubmenuOpen && (
                                <div
                                  className={`absolute z-50 w-[min(18rem,calc(100vw-2rem))] rounded-lg border border-gray-300 bg-white p-2 shadow-xl ${
                                    insightsSubmenuPlacement === 'right'
                                      ? 'left-full top-0'
                                      : insightsSubmenuPlacement === 'left'
                                        ? 'right-full top-0'
                                        : 'right-0 top-full'
                                  }`}
                                  role="menu"
                                  aria-label={t('myInsights')}
                                >
                                  <a
                                    href={districtDashboardUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="group block rounded-md px-3 py-3 text-gray-700 transition-colors hover:bg-primary-50 hover:text-primary-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600"
                                    onClick={() => {
                                      setIsInsightsSubmenuOpen(false)
                                      setIsUserMenuOpen(false)
                                    }}
                                    role="menuitem"
                                  >
                                    <span className="flex items-center justify-between gap-3 text-sm font-semibold">
                                      {t('districtDashboard')}
                                      <ExternalLink className="h-4 w-4 shrink-0" aria-hidden="true" />
                                    </span>
                                    
                                  </a>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      <button
                        onClick={() => { handleLogout(); setIsUserMenuOpen(false); setIsInsightsSubmenuOpen(false) }}
                        className="w-full text-left px-4 py-3 text-sm rounded-b-lg transition-colors"
                        style={{ color: '#7C2D37' }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F5E6E8'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        <LogOut className="h-4 w-4 inline mr-2" />
                        {t('logout')}
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <Link
                    to="/login"
                    state={loginReturnTo ? { returnTo: loginReturnTo } : undefined}
                    onClick={prepareLogin}
                    className={accountControl}
                    aria-label={t('signInCreateAccount')}
                  >
                    <User className="h-5 w-5" aria-hidden="true" />
                    <span>{t('signInCreateAccount')}</span>
                  </Link>
                </div>
              )}

              {/* Language Switcher */}
              <LanguageSwitcher dark={dark} tone={identityHeader ? 'primary' : undefined} />

              {/* Cart */}
              {isAuthenticated && user && (
                <Link
                  to="/cart"
                  className={`relative ${iconBtn}`}
                  title={t('viewShoppingCart')}
                >
                  <ShoppingCart className="h-5 w-5" />
                  {cartCount > 0 && (
                    <span className="absolute -top-1 -right-1 inline-flex items-center justify-center w-5 h-5 text-xs font-bold leading-none rounded-full bg-secondary-500 text-white shadow-md">
                      {cartCount}
                    </span>
                  )}
                </Link>
              )}

              {/* Mobile menu button */}
              <button
                type="button"
                className={`xl:hidden ${iconBtn}`}
                onClick={() => setIsMenuOpen(!isMenuOpen)}
              >
                {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div
            className={`xl:hidden ${mobileBorder}`}
            style={{ background: BRANDING.headerBackground }}
          >
            <div className="px-4 pt-2 pb-3 space-y-1">
              {!(isAuthenticated && user) && (
                <Link to="/login" state={loginReturnTo ? { returnTo: loginReturnTo } : undefined} className={`block px-4 py-2 rounded-lg text-base font-medium transition-all ${location.pathname === '/login' ? mobileActive : mobileHover}`} style={{ color: mobileLinkText }} onClick={prepareLogin}>
                  {t('signInCreateAccount')}
                </Link>
              )}
              <Link
                to="/"
                className={`block px-4 py-2 rounded-lg text-base font-medium transition-all ${location.pathname === '/' ? mobileActive : mobileHover}`}
                style={{ color: mobileLinkText }}
                onClick={() => setIsMenuOpen(false)}
              >
                {t('home')}
              </Link>
              {onDemandOnly ? (
                <Link
                  to="/ondemand"
                  className={`block px-4 py-2 rounded-lg text-base font-medium transition-all ${location.pathname.startsWith('/ondemand') ? mobileActive : mobileHover}`}
                  style={{ color: mobileLinkText }}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t('onDemand')}
                </Link>
              ) : (<>
              {externalCalendarUrl ? (
                <a
                  href={externalCalendarUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`block px-4 py-2 rounded-lg text-base font-medium transition-all ${mobileHover}`}
                  style={{ color: mobileLinkText }}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t('calendar')}
                </a>
              ) : (
                <Link
                  to="/calendar"
                  className={`block px-4 py-2 rounded-lg text-base font-medium transition-all ${location.pathname === '/calendar' ? mobileActive : mobileHover}`}
                  style={{ color: mobileLinkText }}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t('calendar')}
                </Link>
              )}
              {externalCatalogUrl ? (
                <a
                  href={externalCatalogUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`block px-4 py-2 rounded-lg text-base font-medium transition-all ${mobileHover}`}
                  style={{ color: mobileLinkText }}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t('catalog')}
                </a>
              ) : (
                <Link
                  to="/catalog"
                  className={`block px-4 py-2 rounded-lg text-base font-medium transition-all ${location.pathname === '/catalog' ? mobileActive : mobileHover}`}
                  style={{ color: mobileLinkText }}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t('catalog')}
                </Link>
              )}

              <div className="space-y-1">
                <div className="px-4 py-2 text-base font-medium" style={{ color: mobileLinkText }}>{t('sessions')}</div>
                <Link to="/conferences" className={`block pl-8 pr-4 py-2 rounded-lg text-base ${mobileHover} transition-all`} style={{ color: mobileLinkText }} onClick={() => setIsMenuOpen(false)}>{t('conferences')}</Link>
                {TENANT_LANDING.showHybridEvents && (
                  <Link to="/hybrid-events" className={`block pl-8 pr-4 py-2 rounded-lg text-base ${mobileHover} transition-all`} style={{ color: mobileLinkText }} onClick={() => setIsMenuOpen(false)}>{t('hybrid')}</Link>
                )}
                <Link to="/ondemand" className={`block pl-8 pr-4 py-2 rounded-lg text-base ${mobileHover} transition-all`} style={{ color: mobileLinkText }} onClick={() => setIsMenuOpen(false)}>{t('onDemand')}</Link>
                {user?.isEmployee && (
                  <>
                    <div className="pl-8 pr-4 py-2 text-base font-medium" style={{ color: mobileLinkText }}>{t('employee')}</div>
                    <Link to="/workforce-development-wednesday" className={`block pl-12 pr-4 py-2 rounded-lg text-base ${mobileHover} transition-all`} style={{ color: mobileLinkText }} onClick={() => setIsMenuOpen(false)}>{t('workforceDevelopment')}</Link>
                    <Link to="/internal-sessions" className={`block pl-12 pr-4 py-2 rounded-lg text-base ${mobileHover} transition-all`} style={{ color: mobileLinkText }} onClick={() => setIsMenuOpen(false)}>{t('internal')}</Link>
                  </>
                )}
              </div>
              </>)}

              {enableCommunity && (
                <Link
                  to="/community"
                  className={`block px-4 py-2 rounded-lg text-base font-medium transition-all ${location.pathname === '/community' ? mobileActive : mobileHover}`}
                  style={{ color: mobileLinkText }}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t('community')}
                </Link>
              )}

              <a
                href="https://esc4.net/escdevelopworks/#alert"
                target="_blank"
                rel="noopener noreferrer"
                className={`block px-4 py-2 rounded-lg text-base font-medium ${mobileHover} transition-all`}
                style={{ color: mobileLinkText }}
                onClick={() => setIsMenuOpen(false)}
              >
                {t('contactUs')}
              </a>

            </div>
          </div>
        )}
      </header>
    </>
  )
}
